import { ChangeDetectionStrategy, Component, OnChanges, SimpleChanges, input, signal } from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { ColumnConfigEx, ColumnDefinitionEx } from '../../../../@core/types/column-config-types-extended';
import { AccessoryAppliedOnDirection, AccessoryConfig, AccessoryConfigSearchInput, AccessoryConfigType, AccessoryInsertMode, AccessoryQuantityType, Product, ProductGroup } from '../../../../generated/generated-types';
import { BaseListWrapperComponent } from '../../../../shared/components/base-list-wrapper/base-list-wrapper.component';
import { NewBaseListComponent } from '../../../../shared/components/new-base-list-component/new-base-list-component';
import { PhxCheckboxComponent } from '../../../../shared/components/phx-input-checkbox/phx-input-checkbox.component';
import { PhxInputNumberComponent } from '../../../../shared/components/phx-input-number/phx-input-number.component';
import { PhxInputComponent } from '../../../../shared/components/phx-input/phx-input.component';
import { GenericEnumDropDownComponent } from '../../../../shared/controls/generic-enum-drop-down/generic-enum-drop-down.component';
import { subscribeUntilComponentDestroyed } from '../../../../shared/utils/subscribe-until-component-destroyed';
import { ProductDropDownComponent } from '../../modules/product-chooser/product-dropdown.component';
import { AccessoryConfigProductLinkService } from '../../services/accessory-config-product-link.service';
import { AccessoryConfigService } from '../../services/accessory-config.service';
import { setupAccessoryQuantityExpressionPhxInput } from '../../utils/setup-accessory-quantity-expression-phx-input.util';
import {
  isAccessoryConfigQuantityEditable,
  isAccessoryConfigQuantityExpressionEditable,
  normalizeAccessoryConfigQuantityFieldsByType,
} from '../../utils/accessory-config-quantity-fields.util';

@Component({
  selector: 'app-accessory-config-list-widget',
  templateUrl: './accessory-config-list-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    BaseListWrapperComponent,
  ]
})
export class AccessoryConfigListWidgetComponent extends NewBaseListComponent<AccessoryConfig> implements OnChanges {


  product = input<Product>();
  productGroup = input<ProductGroup>();

  withTaxComponent = signal<PhxCheckboxComponent>(null);
  quantityComponent = signal<PhxInputNumberComponent>(null);
  quantityExpressionComponent = signal<PhxInputComponent>(null);
  private syncQuantityExpressionInputState = signal<(() => void) | null>(null);

  constructor(
    private service: AccessoryConfigService,
    private accessoryProductLinkService: AccessoryConfigProductLinkService,
  ) {
    super("AccessoryConfig", service);
  }

  ngOnInit(): void {
    this.noBaseEdit = true;
    this.inlineEditMode = true;
    this.addInDetail = false;
    super.ngOnInit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.accessoryProduct) {
      this.getSearchInputForQuery();
      this.refresh();
    }
  }

  async ngAfterViewInit() {
    super.ngAfterViewInit();
  }

  getSearchInputForQuery() {

    let searchInput: AccessoryConfigSearchInput = super.getSearchInputForQuery();

    if (!this.product())
      return searchInput;

    searchInput.onlyOfProductId = (this.product()).id;

    return searchInput;
  }

  override addNewItem(index?: number, defaultValues?: AccessoryConfig) {
    const newConfig = defaultValues ?? ({} as AccessoryConfig);
    newConfig.direction = AccessoryAppliedOnDirection.BOTH;
    newConfig.insertMode = AccessoryInsertMode.AUTO;
    newConfig.quantityType = AccessoryQuantityType.FIXED;
    newConfig.quantity = 1;
    newConfig.type = AccessoryConfigType.SIMPLE;
    newConfig.updateWhenProductChange = true;
    super.addNewItem(index, newConfig);
  }

  override async saveInCRUDService(rowData: AccessoryConfig): Promise<AccessoryConfig> {
    normalizeAccessoryConfigQuantityFieldsByType(rowData);
    if (rowData.price == null) {
      rowData.withTax = null;
    }
    return super.saveInCRUDService(rowData);
  }

  private syncQuantityFieldComponents(type: AccessoryConfigType | null | undefined): void {
    this.quantityComponent?.()?.disabled.set(!isAccessoryConfigQuantityEditable(type));
    this.quantityExpressionComponent?.()?.disabled.set(!isAccessoryConfigQuantityExpressionEditable(type));
  }

  private applyTypeQuantityRules(config: AccessoryConfig): void {
    normalizeAccessoryConfigQuantityFieldsByType(config);
    this.syncQuantityFieldComponents(config.type);
    if (config.type === AccessoryConfigType.JSONATA) {
      this.quantityComponent?.()?.setValue(null);
    } else {
      this.quantityExpressionComponent?.()?.setValue('');
    }
    this.syncQuantityExpressionInputState()?.();
  }

  protected async setColumnConfig(colConfig: ColumnConfigEx) {

    for (let cd of colConfig.columns) {
      const fieldLowerCase = cd.field?.toLowerCase();

      if (fieldLowerCase === 'type') {
        cd.getEditComponent = () => GenericEnumDropDownComponent;
        cd.editComponentCreated = (c: GenericEnumDropDownComponent, rowData: AccessoryConfig, colDef: ColumnDefinitionEx) => {
          if (rowData.type) {
            c.value.set(rowData.type);
          }
          subscribeUntilComponentDestroyed(c, c.valueChange, (type: AccessoryConfigType) => {
            rowData.type = type;
            this.applyTypeQuantityRules(rowData);
          });
        };
      }

      if (fieldLowerCase === 'quantity') {
        cd.getEditComponent = () => PhxInputNumberComponent;
        cd.editComponentCreated = (c: PhxInputNumberComponent, rowData: AccessoryConfig, colDef: ColumnDefinitionEx) => {
          this.quantityComponent.set(c);
          c.setValue(rowData.quantity);
          c.upDownButtons.set(false);
          c.disabled.set(!isAccessoryConfigQuantityEditable(rowData.type));
        };
      }

      if (fieldLowerCase === 'quantityexpression') {
        cd.getEditComponent = () => PhxInputComponent;
        cd.editComponentCreated = (c: PhxInputComponent, rowData: AccessoryConfig, colDef: ColumnDefinitionEx) => {
          this.quantityExpressionComponent.set(c);
          const syncState = setupAccessoryQuantityExpressionPhxInput({
            input: c,
            accessoryConfig: rowData,
            getProduct: () => this.product(),
            modalService: this.baseServices.modalService,
            host: c,
            dialogTitle: this.baseServices.i18nService.translate(_('accessory-config.quantity-expression-dialog-title')),
            onExpressionChange: (expression) => {
              rowData.quantityExpression = expression;
            },
          });
          this.syncQuantityExpressionInputState.set(syncState);
        };
      }

      if (fieldLowerCase === 'withtax') {
        cd.getEditComponent = () => PhxCheckboxComponent;
        cd.editComponentCreated = (c: PhxCheckboxComponent, rowData: AccessoryConfig, colDef: ColumnDefinitionEx) => {
          this.withTaxComponent.set(c);
          const hasManualPrice = rowData.price != null;
          c.ngModel.set(hasManualPrice ? rowData.withTax : null);
          c.disabled.set(!hasManualPrice);
        };
      }

      if (fieldLowerCase === 'price') {
        cd.getEditComponent = () => PhxInputNumberComponent;
        cd.editComponentCreated = (c: PhxInputNumberComponent, rowData: AccessoryConfig, colDef: ColumnDefinitionEx) => {
          c.setValue(rowData.price);
          if (rowData.price == null) {
            c.placeholder.set(this.baseServices.i18nService.translate(_("accessory-config.price-placeholder")));
          }
          c.minFractionDigits.set(2);
          c.upDownButtons.set(false);
          subscribeUntilComponentDestroyed(c, c.ngModelChange$, (price: number) => {
            rowData.price = price;
            if (price == null) {
              rowData.withTax = null;
              this.withTaxComponent?.()?.ngModel.set(null);
              this.withTaxComponent?.()?.disabled.set(true);
            } else {
              this.withTaxComponent?.()?.disabled.set(false);
            }
          });
        };
      }

      // it has to be accessoryProduct identifier to get the dropdown for choosing the accessoryProduct
      if (cd.field == "accessoryProduct.identifier") {
        cd.getEditComponent = () => ProductDropDownComponent;
        cd.editComponentCreated = async (c: ProductDropDownComponent, rowData: AccessoryConfig, colDef: ColumnDefinitionEx) => {

          if (this.newId === null || this.newId === undefined) {
            c.disabled.set(true);
          }

          if (rowData.accessoryProduct)
            c.setModel(rowData.accessoryProduct);
          else if (rowData.accessoryProductId) {
            await c.loadItemByIdAndSetSelected(rowData.accessoryProductId);
            this.cdRef.detectChanges();
          }
          c.showWarehouseStockButton = true;
          c?.value.subscribe((x: Product) => {
            rowData.accessoryProductId = x?.id;

          })
        }
      }

    }
    super.setColumnConfig(colConfig);
  }

}

